import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import { Toaster } from "./components/ui/sonner";
import "@/App.css";

// Pages
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { AuthCallback } from "./pages/AuthCallback";
import { DashboardPage } from "./pages/DashboardPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { ProjectDetailPage } from "./pages/ProjectDetailPage";
import { TimeReportPage } from "./pages/TimeReportPage";
import { ChangeOrdersPage } from "./pages/ChangeOrdersPage";
import { DrawingsPage } from "./pages/DrawingsPage";
import { IncidentsPage } from "./pages/IncidentsPage";
import { InspectionsPage } from "./pages/InspectionsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { DailyReportsPage } from "./pages/DailyReportsPage";
import { BudgetPage } from "./pages/BudgetPage";
import { GanttPage } from "./pages/GanttPage";
import { TeamPage } from "./pages/TeamPage";
import { KMAPage } from "./pages/KMAPage";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage";
import { AdminLoginPage } from "./pages/admin/AdminLoginPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminOrganizationsPage } from "./pages/admin/AdminOrganizationsPage";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";
import { AdminLogsPage } from "./pages/admin/AdminLogsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Language context
const translations = {
  sv: {
    dashboard: "Översikt",
    projects: "Projekt",
    timeReport: "Tidrapport",
    changeOrders: "ÄTA",
    drawings: "Ritningar",
    incidents: "Avvikelser",
    inspections: "Kontroller",
    settings: "Inställningar",
    login: "Logga in",
    logout: "Logga ut",
    register: "Registrera",
    welcome: "Välkommen",
    activeProjects: "Aktiva projekt",
    pendingTasks: "Väntande uppgifter",
    openIncidents: "Öppna avvikelser",
    pendingChangeOrders: "Väntande ÄTA",
    today: "Idag",
    thisWeek: "Denna vecka",
    create: "Skapa",
    save: "Spara",
    cancel: "Avbryt",
    delete: "Radera",
    edit: "Redigera",
    view: "Visa",
    status: "Status",
    name: "Namn",
    description: "Beskrivning",
    date: "Datum",
    hours: "Timmar",
    budget: "Budget",
    cost: "Kostnad",
    phase: "Etapp",
    activity: "Aktivitet",
    task: "Uppgift",
    search: "Sök",
    filter: "Filtrera",
    export: "Exportera",
    noData: "Ingen data tillgänglig",
    loading: "Laddar...",
    error: "Ett fel uppstod",
    success: "Klart!",
    confirm: "Bekräfta",
  },
  en: {
    dashboard: "Dashboard",
    projects: "Projects",
    timeReport: "Time Report",
    changeOrders: "Change Orders",
    drawings: "Drawings",
    incidents: "Incidents",
    inspections: "Inspections",
    settings: "Settings",
    login: "Login",
    logout: "Logout",
    register: "Register",
    welcome: "Welcome",
    activeProjects: "Active Projects",
    pendingTasks: "Pending Tasks",
    openIncidents: "Open Incidents",
    pendingChangeOrders: "Pending Change Orders",
    today: "Today",
    thisWeek: "This Week",
    create: "Create",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    view: "View",
    status: "Status",
    name: "Name",
    description: "Description",
    date: "Date",
    hours: "Hours",
    budget: "Budget",
    cost: "Cost",
    phase: "Phase",
    activity: "Activity",
    task: "Task",
    search: "Search",
    filter: "Filter",
    export: "Export",
    noData: "No data available",
    loading: "Loading...",
    error: "An error occurred",
    success: "Success!",
    confirm: "Confirm",
  }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Language Context
const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    if (token) {
      try {
        const res = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        });
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
        } else {
          localStorage.removeItem("token");
          setToken(null);
        }
      } catch (e) {
        console.error("Auth check failed:", e);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, [token]);

  const login = (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    if (accessToken) {
      localStorage.setItem("token", accessToken);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Language Provider
const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(localStorage.getItem("lang") || "sv");

  const setLanguage = (lang) => {
    setLanguageState(lang);
    localStorage.setItem("lang", lang);
  };

  const t = (key) => {
    return translations[language]?.[key] || translations.sv[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Laddar...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id - must be synchronous to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
      <Route path="/time-report" element={<ProtectedRoute><TimeReportPage /></ProtectedRoute>} />
      <Route path="/change-orders" element={<ProtectedRoute><ChangeOrdersPage /></ProtectedRoute>} />
      <Route path="/drawings" element={<ProtectedRoute><DrawingsPage /></ProtectedRoute>} />
      <Route path="/incidents" element={<ProtectedRoute><IncidentsPage /></ProtectedRoute>} />
      <Route path="/inspections" element={<ProtectedRoute><InspectionsPage /></ProtectedRoute>} />
      <Route path="/daily-reports" element={<ProtectedRoute><DailyReportsPage /></ProtectedRoute>} />
      <Route path="/budget" element={<ProtectedRoute><BudgetPage /></ProtectedRoute>} />
      <Route path="/gantt" element={<ProtectedRoute><GanttPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/kma" element={<ProtectedRoute><KMAPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboardPage />} />
        <Route path="organizations" element={<AdminOrganizationsPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="logs" element={<AdminLogsPage />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Register Service Worker for offline support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(reg => console.log('SW registered:', reg.scope))
      .catch(err => console.log('SW registration failed:', err));
  });
}

function App() {
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    setIsOffline(!navigator.onLine);
    return () => { window.removeEventListener('offline', goOffline); window.removeEventListener('online', goOnline); };
  }, []);

  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          {isOffline && (
            <div className="fixed top-0 inset-x-0 z-[100] bg-amber-500 text-white text-center py-1.5 text-sm font-medium" data-testid="offline-banner">
              Du är offline — vissa funktioner kan vara begränsade
            </div>
          )}
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
